<script lang="ts">
  import UiDialog from '$lib/components/ui/dialog.svelte'
  import type { CrossVideoEntry, EntityType, User } from '../lib/types'
  import { hasPermission } from '../lib/types'

  interface Props {
    open: boolean
    entityName: string
    entityType: EntityType
    entityId: number | null
    currentVideoId: string | null
    currentUser?: User | null
    onClose: () => void
  }

  let {
    open = $bindable(false),
    entityName,
    entityType,
    entityId,
    currentVideoId,
    currentUser = null,
    onClose,
  }: Props = $props()

  let entries = $state<CrossVideoEntry[]>([])
  let loading = $state(false)
  let error = $state<string | null>(null)

  const TYPE_MAP: Record<EntityType, string> = {
    person: 'persons',
    event: 'events',
    topic: 'topics',
    other: 'others',
  }

  const COLOR_MAP: Record<EntityType, string> = {
    person: 'purple',
    event: 'amber',
    topic: 'green',
    other: 'orange',
  }

  $effect(() => {
    if (open && entityId) {
      fetchVideos()
    }
  })

  async function fetchVideos() {
    loading = true
    error = null
    try {
      const response = await fetch(`/api/mentionables/${TYPE_MAP[entityType]}/${entityId}/videos`)
      if (!response.ok) throw new Error('Failed to fetch')
      entries = await response.json()
    } catch (e) {
      error = 'Could not load cross-video data'
      entries = []
    } finally {
      loading = false
    }
  }

  function formatTime(seconds: number): string {
    const hours = Math.floor(seconds / 3600)
    const mins = Math.floor((seconds % 3600) / 60)
    const secs = Math.floor(seconds % 60)
    if (hours > 0) {
      return `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  function navigateToVideo(youtubeId: string, timecode?: number) {
    const url = new URL(window.location.href)
    url.searchParams.set('v', youtubeId)
    if (timecode !== undefined) {
      url.searchParams.set('t', `${Math.floor(timecode)}s`)
    } else {
      url.searchParams.delete('t')
    }
    window.location.href = url.toString()
  }

  let color = $derived(COLOR_MAP[entityType])
</script>

<UiDialog bind:open onOpenChange={(o) => { if (!o) onClose() }} title={entityName} class="max-w-[600px]">
  {#if entityId !== null && hasPermission(currentUser, 'can_view_graph')}
    <div class="pb-3 border-b border-border-default mb-3">
      <a
        href={`/graph?entity_id=${entityId}`}
        class="text-sm text-text-secondary hover:text-text-primary underline"
        data-testid="open-in-graph"
      >
        Bekijk in graph view →
      </a>
    </div>
  {/if}
  {#if loading}
    <div class="flex items-center justify-center py-8">
      <span class="text-sm text-text-muted">Loading...</span>
    </div>
  {:else if error}
    <div class="text-sm text-red-400 py-4">{error}</div>
  {:else}
    <div class="space-y-3 max-h-[60vh] overflow-y-auto">
      {#each entries as entry}
        {@const isCurrent = entry.youtube_id === currentVideoId}
        <div class="rounded-lg border p-3 transition-colors
          {isCurrent ? `bg-${color}-500/20 border-${color}-500/50` : 'bg-surface-3 border-border-default hover:border-border-hover'}">
          <div class="flex gap-3">
            <button
              class="shrink-0 w-[120px] h-[68px] rounded overflow-hidden bg-surface-4 border-none cursor-pointer p-0"
              onclick={() => navigateToVideo(entry.youtube_id)}
            >
              <img
                src="https://i.ytimg.com/vi/{entry.youtube_id}/mqdefault.jpg"
                alt="Thumbnail"
                class="w-full h-full object-cover"
              />
            </button>
            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-2 mb-1">
                <button
                  class="text-sm font-medium text-text-primary hover:text-accent-primary truncate bg-transparent border-none cursor-pointer p-0 text-left"
                  onclick={() => navigateToVideo(entry.youtube_id)}
                >
                  {entry.youtube_id}
                </button>
                {#if isCurrent}
                  <span class="text-[10px] px-1.5 py-0.5 bg-{color}-500/30 text-{color}-300 rounded shrink-0">current</span>
                {/if}
                <span class="text-[10px] px-1.5 py-0.5 bg-surface-4 text-text-muted rounded shrink-0">
                  {entry.mention_count} mention{entry.mention_count === 1 ? '' : 's'}
                </span>
              </div>
              {#if entry.description}
                <p class="text-xs text-text-secondary leading-relaxed line-clamp-2 m-0">
                  {entry.description.slice(0, 250)}{entry.description.length > 250 ? '...' : ''}
                </p>
              {/if}
            </div>
          </div>

          {#if entry.mentions.length > 0 && entry.duration > 0}
            <div class="mt-2 h-2 bg-surface-2 rounded relative">
              {#each entry.mentions as mention}
                <button
                  class="absolute w-1.5 h-1.5 rounded-full top-1/2 -translate-y-1/2 cursor-pointer transition-opacity hover:opacity-80
                    {entityType === 'person' ? 'bg-purple-400' : ''}
                    {entityType === 'event' ? 'bg-amber-400' : ''}
                    {entityType === 'topic' ? 'bg-green-400' : ''}
                    {entityType === 'other' ? 'bg-orange-400' : ''}"
                  style="left: {(mention.timecode / entry.duration) * 100}%"
                  onclick={() => navigateToVideo(entry.youtube_id, mention.timecode)}
                  title="{formatTime(mention.timecode)} — {mention.used_name}"
                ></button>
              {/each}
            </div>
          {/if}

          {#if entry.snippet_text}
            <p class="mt-2 text-xs text-text-muted leading-relaxed line-clamp-2 m-0 italic">
              &ldquo;[...] {entry.snippet_text} [...]&rdquo;
            </p>
          {/if}
        </div>
      {/each}
    </div>
  {/if}
</UiDialog>
